import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Upload, Trash2, ImageIcon, ZoomIn, ZoomOut, Maximize2, Loader2 } from "lucide-react";
import axios from "axios";

import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ToBeProcessFlow = () => {
  const { selectedProduct } = useAuth();
  const productId = selectedProduct?.id || "global";
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [fullscreen, setFullscreen] = useState(false);
  const fileRef = useRef(null);

  const imageKey = `tobe_flow_image_${productId}`;

  const fetchImage = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cloudinary/get-url/${imageKey}`);
      if (r.data.url) setImageUrl(r.data.url);
      else setImageUrl("");
    } catch (e) {
      console.error(e);
    }
  }, [imageKey]);

  useEffect(() => {
    fetchImage();
  }, [fetchImage]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);

    try {
      const sigRes = await axios.get(`${API}/cloudinary/signature?folder=tobe-flow`);
      const sig = sigRes.data;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("api_key", sig.api_key);
      formData.append("timestamp", sig.timestamp);
      formData.append("signature", sig.signature);
      formData.append("folder", sig.folder);

      const cloudinaryRes = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        { method: "POST", body: formData }
      );
      const cloudinaryData = await cloudinaryRes.json();

      if (cloudinaryData.secure_url) {
        await axios.post(`${API}/cloudinary/save-url`, {
          key: imageKey,
          url: cloudinaryData.secure_url,
          public_id: cloudinaryData.public_id
        });
        setImageUrl(cloudinaryData.secure_url);
        setZoom(100);
      }
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Upload failed. Please try again.");
    }

    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const deleteImage = async () => {
    try {
      await axios.post(`${API}/cloudinary/save-url`, {
        key: imageKey,
        url: "",
        public_id: ""
      });
      setImageUrl("");
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div data-testid="tobe-process-flow-panel">
      <p className="text-xs font-semibold tracking-widest uppercase text-[#3B82F6] mb-3">Proposed State</p>
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 dark:text-slate-100 font-['Outfit']">To-Be Experience Model</h1>
      <p className="text-base text-slate-500 dark:text-slate-400 mt-2">The enhanced workflow with automation layer</p>
      <Separator className="my-8 bg-slate-200 dark:bg-slate-700" />

      <Card className="border border-slate-200 shadow-sm bg-white" data-testid="tobe-flow-diagram">
        <CardContent className="p-6">
          {imageUrl ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setZoom((z) => Math.max(25, z - 25))} className="h-8 w-8 p-0" data-testid="tobe-zoom-out"><ZoomOut className="w-4 h-4" /></Button>
                  <span className="text-xs font-medium text-slate-500 w-12 text-center">{zoom}%</span>
                  <Button variant="outline" size="sm" onClick={() => setZoom((z) => Math.min(200, z + 25))} className="h-8 w-8 p-0" data-testid="tobe-zoom-in"><ZoomIn className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => setFullscreen(true)} className="h-8 px-3" data-testid="tobe-fullscreen"><Maximize2 className="w-4 h-4 mr-1" />Fullscreen</Button>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="h-8" data-testid="tobe-replace-btn"><Upload className="w-3.5 h-3.5 mr-1" />Replace</Button>
                  <Button variant="outline" size="sm" onClick={deleteImage} className="h-8 text-red-500 hover:text-red-600 hover:bg-red-50" data-testid="tobe-delete-btn"><Trash2 className="w-3.5 h-3.5 mr-1" />Remove</Button>
                </div>
              </div>
              <div className="overflow-auto border border-slate-100 rounded-lg bg-slate-50 max-h-[75vh]" data-testid="tobe-image-container">
                <img src={imageUrl} alt="To-Be Experience Model" style={{ width: `${zoom}%`, minWidth: "100%" }} className="block" data-testid="tobe-flow-image" />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-[#3B82F6] hover:bg-blue-50/30 transition-all"
              onClick={() => !uploading && fileRef.current?.click()} data-testid="tobe-upload-area">
              {uploading ? (
                <>
                  <Loader2 className="w-12 h-12 text-[#3B82F6] mb-4 animate-spin" />
                  <p className="text-sm font-semibold text-slate-600 mb-1">Uploading to Cloudinary...</p>
                  <p className="text-xs text-slate-400">Please wait</p>
                </>
              ) : (
                <>
                  <ImageIcon className="w-12 h-12 text-slate-300 mb-4" />
                  <p className="text-sm font-semibold text-slate-600 mb-1">Upload To-Be Experience Model Diagram</p>
                  <p className="text-xs text-slate-400">Click to upload an image (PNG, JPG, SVG, GIF)</p>
                </>
              )}
            </div>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="tobe-file-input" />
        </CardContent>
      </Card>

      {/* Fullscreen modal */}
      {fullscreen && imageUrl && (
        <div className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center cursor-pointer" onClick={() => setFullscreen(false)} data-testid="tobe-fullscreen-modal">
          <img src={imageUrl} alt="To-Be Experience Model" className="max-w-[95vw] max-h-[95vh] object-contain" />
        </div>
      )}
    </div>
  );
};
